import { datadogRum } from '@datadog/browser-rum';
import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import routeNameComponentLookup from '|/app/purchase/flow/step-registry';
import { Step } from '|/app/services';
import ProtectedRoute from '|/components/base/protect-route';
import { setCurrentStepName } from '|/helpers/current-step-tracker';
import useChatPageContext from './use-chat-page-context';

const HideableStep = styled.div<{ isHidden: boolean }>`
  ${props =>
    props.isHidden &&
    css`
      display: none;
    `}
  height: 100%;
`;

const StepStartTracker = ({
  stepName,
  stepId
}: {
  stepName: string;
  stepId: Step['id'] | null;
}) => {
  useChatPageContext(stepName, stepId);

  useEffect(() => {
    const currentPath = window.location.pathname;
    datadogRum.addAction('step_start', {
      stepFullUrl: currentPath,
      stepName
    });
    // Only own the checkoutStep tag when on the legacy /steps/* route. In the
    // /checkout flow, StepElement is also mounted inside the verifications
    // modal; useCheckoutVm and useVerificationsVm own the tag there.
    if (!stepName || !currentPath.includes('/steps/')) return;
    setCurrentStepName(stepName);
    datadogRum.setGlobalContextProperty('checkoutStep', stepName);
    return () => {
      datadogRum.removeGlobalContextProperty('checkoutStep');
    };
  }, [stepName]);

  return null;
};

const defaultStepProps = {
  step: null as null | Step,
  isHidden: false,
  GetDashboardAndRouteToNextStep: () => {
    /* intentionally empty */
  }
};

const StepElement = ({ step, isHidden, GetDashboardAndRouteToNextStep } = defaultStepProps) => {
  const { id = null } = step ?? {};

  const stepConfig = id === null ? null : routeNameComponentLookup[id];

  if (stepConfig === null) {
    return null;
  }

  const renderStep = () => {
    if ('override' in stepConfig) {
      return stepConfig.override({ step });
    }

    return (
      <stepConfig.component
        GetDashboardAndRouteToNextStep={() => ({
          type: 'GET_DASHBOARD',
          payload: GetDashboardAndRouteToNextStep
        })}
      />
    );
  };

  return (
    <ProtectedRoute>
      <StepStartTracker stepName={step?.url ?? ''} stepId={step?.id ?? null} />
      <HideableStep isHidden={isHidden}>{renderStep()}</HideableStep>
    </ProtectedRoute>
  );
};

export default StepElement;
